import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_SIGN_IN } from '@/constants/routes';
import authService from '@/services/auth.service';
import envService from '@/services/env.service';
import { getSafeRedirect } from '@/utils/safe-redirect';

interface VerifySignedLinkResponse {
  token: string;
  redirectTo: string;
}

interface VerifySignedLinkErrorDetail {
  error: 'link_expired' | 'invalid_link';
  redirectTo: string | null;
}

export default function AuthMagicPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const redirectFromUrl = getSafeRedirect(searchParams.get('redirect'));

    if (!token) {
      navigate(`${ROUTE_SIGN_IN}?error=invalid_link`);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(
          `${envService.getEnv().VITE_CATCH_BACKEND_API_URL}/v1/auth/verify-signed-link`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          }
        );

        if (!response.ok) {
          const errorData = (await response.json()) as { detail: VerifySignedLinkErrorDetail };
          const detail = errorData.detail;

          if (detail.error === 'link_expired') {
            // Preserve redirect for manual login
            const redirectParam = detail.redirectTo
              ? `&redirect=${encodeURIComponent(detail.redirectTo)}`
              : '';
            navigate(`${ROUTE_SIGN_IN}?error=link_expired${redirectParam}`);
          } else {
            navigate(`${ROUTE_SIGN_IN}?error=invalid_link`);
          }
          return;
        }

        const data = (await response.json()) as VerifySignedLinkResponse;

        // Store JWT and navigate to redirect destination
        authService.login({ token: data.token });
        navigate(getSafeRedirect(data.redirectTo) || redirectFromUrl);
      } catch {
        setError('An unexpected error occurred. Please try again.');
        navigate(`${ROUTE_SIGN_IN}?error=invalid_link`);
      }
    };

    verifyToken();
  }, [navigate, searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      {error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      )}
    </div>
  );
}
